import {
  TranslationKey,
  useTranslate,
  UseTranslateType,
} from 'module/hooks/use-translation';
import {
  faDistributeSpacingVertical,
  faQuestionCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { Editor, Range } from '@tiptap/core';
import {
  CustomSectionNodeType,
  CustomSectionType,
} from 'api/custom-section/types';
import { CustomGroupSectionType } from 'api/custom-section/types/group-section';
import { snakeCase } from 'lodash';
import { useMemo } from 'react';
import { CommandListItemIcon } from 'components/organisms/document-editor/extensions/command-picker/commands-list/common';
import { isAllowedOnlyAtFirstLevel } from 'components/organisms/document-editor/extensions/command-picker/commands-list/utils';
import { CommandPickerItem } from 'components/organisms/document-editor/extensions/command-picker/types';
import { defaultGroupSectionConfig } from 'components/organisms/document-editor/extensions/custom-group-section/default-config';
import { defaultCustomSectionNodeConfig } from 'components/organisms/document-editor/extensions/custom-section/utils';

const positionToInsert = (editor: Editor, range: Range) => {
  const $pos = editor.state.doc.resolve(range.from);
  const insertingIntoGroup =
    $pos.parent.type.name === CustomSectionNodeType.RlGroupSection;

  if (insertingIntoGroup) {
    return range.from;
  }
  const nodeBefore = $pos.nodeBefore;

  if (nodeBefore?.type.name === CustomSectionNodeType.RlGroupSection) {
    const prevGroupStart = $pos.pos - nodeBefore.nodeSize;
    const prevGroupEnd = prevGroupStart + nodeBefore.nodeSize - 1;

    return prevGroupEnd;
  }

  return range.from;
};
const customSectionCommandExecutor =
  (sectionType: CustomSectionType) => (editor: Editor, range: Range) => {
    editor
      .chain()
      .focus()
      .addCustomSection({
        insertAt: positionToInsert(editor, range),
        attributes: defaultCustomSectionNodeConfig[sectionType],
      })
      .run();
  };
const groupCommandExecutor = (editor: Editor, range: Range) => {
  editor
    .chain()
    .focus()
    .addCustomGroupSection({
      insertAt: range.from,
      attributes:
        defaultGroupSectionConfig[CustomGroupSectionType.CustomHeadingSection],
    })
    .run();
};
const isQuestionCommandAllowed = (editor: Editor, range: Range) => {
  const $pos = editor.state.doc.resolve(range.from);
  const isParagraph = $pos.parent.type.name === 'paragraph';
  const isDoc = $pos.parent.type.name === 'doc';
  const startsWithSlash = $pos.parent.textContent?.charAt(0) === '/';
  const isStartOfDocOrParagraph = isDoc || (isParagraph && startsWithSlash);
  const isNodeBeforeGroup =
    $pos.nodeBefore?.type.name === CustomSectionNodeType.RlGroupSection;
  const isParentGroupSection =
    $pos.parent.type.name === CustomSectionNodeType.RlGroupSection;

  return (isStartOfDocOrParagraph && isNodeBeforeGroup) || isParentGroupSection;
};
const isGroupCommandAllowed = isAllowedOnlyAtFirstLevel;

enum DefaultBlockCommand {
  Question = 'question',
  Group = 'group',
}
const DefaultBlockCommandMap = {
  [DefaultBlockCommand.Question]: {
    icon: <CommandListItemIcon icon={faQuestionCircle} />,
    execute: customSectionCommandExecutor(
      CustomSectionType.PeoplePageSourceDerivedQuestionSection,
    ),
    isAllowed: isQuestionCommandAllowed,
  },
  [DefaultBlockCommand.Group]: {
    icon: <CommandListItemIcon icon={faDistributeSpacingVertical} />,
    execute: groupCommandExecutor,
    isAllowed: isGroupCommandAllowed,
  },
};
const sectionCommands = [DefaultBlockCommand.Question];
const groupCommands = [DefaultBlockCommand.Group];
const defaultBlockCommandAsItem = (
  command: DefaultBlockCommand,
  translate: UseTranslateType,
) => ({
  id: command,
  label: translate(
    `people_page_templates.commands.default_ai_block.${snakeCase(command)}.label` as TranslationKey,
  ),
  icon: DefaultBlockCommandMap[command].icon,
  execute: DefaultBlockCommandMap[command].execute,
  isAllowed: DefaultBlockCommandMap[command].isAllowed,
});

export const useDefaultBlockCommandsForPeoplePageTemplate =
  (): CommandPickerItem[] => {
    const translate = useTranslate();

    return useMemo(
      () => [
        {
          id: 'ai-section-commands',
          label: translate(
            'people_page_templates.commands.default_ai_block.label',
          ),
          items: sectionCommands.map((command) =>
            defaultBlockCommandAsItem(command, translate),
          ),
        },
        {
          id: 'group-section-commands',
          label: translate(
            'people_page_templates.commands.default_group_section.label',
          ),
          items: groupCommands.map((command) =>
            defaultBlockCommandAsItem(command, translate),
          ),
        },
      ],
      [translate],
    );
  };
